import React, { useState } from 'react';
import { Box, Typography, Button, Grid, IconButton, Paper, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

const tabs: { id: 'sobre' | 'funcionamento' | 'melhorias' | 'relatorios' | 'contato'; label: string }[] = [
  { id: 'sobre', label: 'NITE' },
  { id: 'funcionamento', label: 'NEPE' },
  { id: 'melhorias', label: 'NAPSA' },
  { id: 'relatorios', label: 'ECOS' },
];

const NucleoApoio: React.FC = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState<'sobre' | 'funcionamento' | 'melhorias' | 'relatorios' | 'contato'>('sobre');
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);

  const underline = {
    content: '""',
    position: 'absolute' as const,
    left: '50%',
    transform: 'translateX(-50%)',
    bottom: 0,
    width: 120,
    height: 4,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.secondary.main,
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'sobre':
        return (
          <>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
              Núcleo de Inovação Tecnológica, Trabalhabilidade e Empreendedorismo
            </Typography>
            <Typography paragraph textAlign="justify" sx={{textIndent: '2em', lineHeight: 1.8 }}>
              O NITE da Faculdade de Tecnologia SENAC Amazonas é um espaço de conexão entre academia e mercado. Atua na promoção da inovação, no estímulo ao empreendedorismo e no apoio à inserção profissional de estudantes e egressos. Por meio de projetos, eventos e parcerias estratégicas, o NITE transforma ideias em soluções, fomenta o desenvolvimento regional e prepara profissionais críticos, inovadores e socialmente responsáveis.
            </Typography>
            <Typography paragraph textAlign="justify" sx={{ lineHeight: 1.8 }}>
              E-mail: nite.faculdade@am.senac.br
            </Typography>
            <Typography paragraph textAlign="justify" sx={{ lineHeight: 1.8 }}>
              Contato: (92) 3199-9956
            </Typography>
          </>
        );

case 'funcionamento':
  return (
    <>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
        Núcleo de Ensino, Pesquisa e Extensão
      </Typography>
      <Typography paragraph textAlign="justify" sx={{ textIndent: '2em', lineHeight: 1.8 }}>
        O Núcleo de Ensino, Pesquisa e Extensão (NEPE) da Faculdade de Tecnologia Senac Amazonas é o setor responsável
        por integrar os pilares fundamentais da educação superior: ensino, pesquisa e extensão. Sua missão é contribuir
        para a construção do conhecimento científico, tecnológico e sociocultural, fortalecendo a relação entre a
        instituição e a comunidade.
      </Typography>
      <Typography paragraph textAlign="justify" sx={{ textIndent: '2em', lineHeight: 1.8 }}>
        O NEPE tem o objetivo de contribuir para a construção do conhecimento científico nos diferentes aspectos:
        sustentáveis, socioculturais, cidadania e empreendedor. As pesquisas desenvolvidas possibilitam o trabalho com
        alunos de pós-graduação, em nível de lato sensu; e, de graduação tecnológica, com orientações desde a iniciação
        científica até os trabalhos de conclusão de curso. Além disso, esses estudos permitem a realização de um trabalho
        multidisciplinar, transversal, integrador, interdepartamental e interinstitucional. (Senac, 2022, p.5)
      </Typography>

      <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
        Objetivos
      </Typography>
      <Box component="ul" sx={{ pl: 3, lineHeight: 1.8 }}>
        <li>Promover a integração entre ensino, pesquisa e extensão, alinhada às políticas institucionais;</li>
        <li>Incentivar a produção científica e a inovação tecnológica;</li>
        <li>Estabelecer parcerias interdepartamentais e interinstitucionais;</li>
        <li>Valorizar a diversidade cultural, social e ambiental da Amazônia;</li>
        <li>Contribuir para o desenvolvimento econômico e social da região.</li>
      </Box>

      <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mt: 3 }}>
        Principais Ações do NEPE
      </Typography>

      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
        Iniciação Científica
      </Typography>
      <Typography paragraph textAlign="justify" sx={{ textIndent: '2em', lineHeight: 1.8 }}>
        Programa que incentiva estudantes a iniciarem atividades de pesquisa, proporcionando o desenvolvimento de
        habilidades investigativas e pensamento crítico, em consonância com as diretrizes institucionais.
      </Typography>

      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
        Projetos de Extensão
      </Typography>
      <Typography paragraph textAlign="justify" sx={{ textIndent: '2em', lineHeight: 1.8 }}>
        Ações de caráter social, cultural, ambiental e tecnológico, promovendo a interação entre comunidade acadêmica e
        sociedade, com foco em inclusão e desenvolvimento regional.
      </Typography>

      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
        Monitoria Acadêmica
      </Typography>
      <Typography paragraph textAlign="justify" sx={{ textIndent: '2em', lineHeight: 1.8 }}>
        Oportunidade para alunos apoiarem atividades pedagógicas, reforçando o aprendizado e a formação acadêmica, ao
        mesmo tempo em que contribuem com a qualidade do ensino.
      </Typography>

      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
        Publicações Científicas
      </Typography>
      <Typography paragraph textAlign="justify" sx={{ textIndent: '2em', lineHeight: 1.8 }}>
        O NEPE estimula e apoia a produção acadêmica da comunidade Senac Amazonas, promovendo a divulgação de artigos,
        trabalhos de pesquisa e relatos em periódicos científicos, congressos e na Revista Científica da Faculdade de
        Tecnologia Senac Amazonas.
      </Typography>

      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
        Cursos e Consultorias
      </Typography>
      <Typography paragraph textAlign="justify" sx={{ textIndent: '2em', lineHeight: 1.8 }}>
        Promoção de cursos de extensão, consultorias e projetos inovadores que buscam soluções tecnológicas, sociais e
        empresariais, alinhados ao desenvolvimento sustentável da região.
      </Typography>

      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
        Intercâmbio e Participação em Eventos
      </Typography>
      <Typography paragraph textAlign="justify" sx={{ textIndent: '2em', lineHeight: 1.8 }}>
        Fomento ao intercâmbio institucional, participação em congressos, seminários, workshops e demais eventos que
        promovam a troca de conhecimentos e experiências entre pesquisadores, docentes e discentes.
      </Typography>

      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
        📲 Canal do NEPE
      </Typography>
      <Typography paragraph textAlign="justify" sx={{ textIndent: '2em', lineHeight: 1.8 }}>
        Siga o canal do NEPE no WhatsApp e receba editais, eventos, cursos, chamadas de artigos, oportunidades,
        voluntariado e muito mais!{' '}
        <a href="https://whatsapp.com/channel/0029Vb0NqBeDDmFWufiaco3v" target="_blank" rel="noopener noreferrer">
          https://whatsapp.com/channel/0029Vb0NqBeDDmFWufiaco3v
        </a>
      </Typography>

      <Typography paragraph sx={{ lineHeight: 1.8 }}>
        <strong>E-mail:</strong> nepe.faculdade@am.senac.br
      </Typography>
      <Typography paragraph sx={{ lineHeight: 1.8 }}>
        <strong>Contato:</strong> (92) 3199-9956
      </Typography>
    </>
  );


      case 'melhorias':
        return (
          <>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
              Núcleo de Apoio Psicopedagógico e Serviços de Acessibilidade
            </Typography>
            <Typography paragraph textAlign="justify" sx={{textIndent: '2em', lineHeight: 1.8 }}>
              O NAPSA é o espaço de acolhimento e cuidado da Faculdade de Tecnologia SENAC Amazonas, promovendo o bem-estar emocional, a inclusão e o sucesso acadêmico. Com uma equipe especializada, oferece apoio psicopedagógico, psicológico e educacional a estudantes e colaboradores. Atua na superação de dificuldades de aprendizagem, acessibilidade, orientação profissional e mediação de conflitos. Atende, com sigilo e empatia, pessoas com deficiências, TEA, altas habilidades e outras necessidades específicas. Também apoia docentes na construção de práticas pedagógicas mais inclusivas. No NAPSA, cada trajetória é respeitada e acompanhada com atenção individualizada. Precisa de apoio? O NAPSA está aqui para você.
            </Typography>
            <Typography paragraph textAlign="justify" sx={{ lineHeight: 1.8 }}>
              E-mail: napsa.faculdade@am.senac.br
            </Typography>
            <Typography paragraph textAlign="justify" sx={{ lineHeight: 1.8 }}>
              Contato: (92) 3199-9956
            </Typography>
          </>
        );
      
      case 'relatorios':
  return (
    <>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
        Programa de Sustentabilidade ECOS
      </Typography>
      <Typography paragraph textAlign="justify" sx={{ textIndent: '2em', lineHeight: 1.8 }}>
        As Políticas de Educação Ambiental da Faculdade de Tecnologia Senac Amazonas foram elaboradas em consonância
        com a legislação vigente, integrando os princípios e objetivos da Política Nacional de Educação Ambiental (PNEA).
      </Typography>
      <Typography paragraph textAlign="justify" sx={{ textIndent: '2em', lineHeight: 1.8 }}>
        Nosso propósito é orientar, fortalecer e subsidiar ações de educação ambiental que promovam uma atuação
        educativa transformadora e sustentável, preparando cidadãos comprometidos com o presente e o futuro.
      </Typography>

      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
        Diretrizes que nos guiam:
      </Typography>
      <Box component="ul" sx={{ pl: 3, lineHeight: 1.8 }}>
        <li>Equidade social e inclusão democrática</li>
        <li>Solidariedade e cooperação entre indivíduos e grupos sociais</li>
        <li>Ética, diálogo de saberes e valorização da diversidade cultural e tradicional</li>
        <li>Integração entre educação, trabalho e práticas sociais</li>
        <li>Contextualização ambiental em níveis local, regional e global</li>
        <li>Construção de uma cultura crítica, participativa e sustentável</li>
      </Box>

      <Typography paragraph textAlign="justify" sx={{ textIndent: '2em', lineHeight: 1.8, mt: 2 }}>
          O Programa ECOS de Sustentabilidade, iniciativa nacional do Senac, é integrado às atividades dos cursos de
        graduação da Faculdade. A partir dele, nossos alunos são incentivados a participar de práticas colaborativas e
        intersetoriais, como campanhas socioeducativas, coleta seletiva, projetos de inovação verde e ações de impacto
        social.
      </Typography>
      <Typography paragraph textAlign="justify" sx={{ textIndent: '2em', lineHeight: 1.8 }}>
          Na Faculdade Senac Amazonas, sustentabilidade é prática, compromisso e transformação!
      </Typography>
    </>
  );


      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 8 } }}>
      {/* Título principal com traço laranja */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 700,
            letterSpacing: 0.2,
            display: 'inline-block',
            pb: 1.5,
            position: 'relative',
            '&::after': underline,
          }}
        >
          NUADIS - Núcleo de Apoio ao Discente
        </Typography>
      </Box>

      {/* Botão hamburguer (mobile) */}
      <Box display={{ xs: 'block', md: 'none' }} textAlign="right" mb={2}>
        <IconButton onClick={() => setMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
        </IconButton>
      </Box>

      <Grid container spacing={4}>
        <Grid
          item
          xs={12}
          md={3}
          display={{ xs: isMobileMenuOpen ? 'block' : 'none', md: 'block' }}
        >
          <Box display="flex" flexDirection="column" gap={2}>
            {tabs.map((tab) => (
              <Button
                key={tab.id}
                variant={activeTab === tab.id ? 'contained' : 'outlined'}
                onClick={() => {
                  setActiveTab(tab.id);
                  setMobileMenuOpen(false);
                }}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                {tab.label}
              </Button>
            ))}
          </Box>
        </Grid>

        <Grid item xs={12} md={9}>
          <Paper sx={{ p: { xs: 2, md: 4 } }}>
            {renderContent()}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default NucleoApoio;
